(() => {
  const FORM_SELECTOR = 'form.newsletter-form';
  const INPUT_SELECTOR = 'input[type="email"][name="contact[email]"]';
  const EMAIL_PATTERN = /^[^\s@]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+$/;
  const WATCHERS = new WeakMap();

  const normalizeEmail = (value) => (value || '').replace(/\s+/g, '').trim();

  const isAutofilled = (input) => {
    try {
      return input.matches(':-webkit-autofill');
    } catch (error) {
      return false;
    }
  };

  const syncFieldState = (input) => {
    if (!input) return;

    const field = input.closest('.field');
    if (!field) return;

    const hasValue = isAutofilled(input) || normalizeEmail(input.value).length > 0;
    field.classList.toggle('is-filled', hasValue);
    input.toggleAttribute('data-has-value', hasValue);
  };

  const applyValidationState = (input) => {
    if (!input) return true;

    const normalizedValue = normalizeEmail(input.value);

    if (normalizedValue.length === 0) {
      input.setCustomValidity('Ingresa tu correo electrónico.');
      return false;
    }

    if (!EMAIL_PATTERN.test(normalizedValue)) {
      input.setCustomValidity('Ingresa un correo electrónico válido, por ejemplo nombre@dominio.com.');
      return false;
    }

    input.setCustomValidity('');
    return true;
  };

  const normalizeInputValue = (input, options = {}) => {
    if (!input) return;

    const { preserveSelection = true } = options;
    const rawValue = input.value || '';
    const normalizedValue = normalizeEmail(rawValue);

    if (rawValue === normalizedValue) return;

    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;

    input.value = normalizedValue;

    if (
      preserveSelection &&
      typeof selectionStart === 'number' &&
      typeof selectionEnd === 'number' &&
      document.activeElement === input
    ) {
      const safePosition = Math.min(selectionEnd, normalizedValue.length);
      input.setSelectionRange(safePosition, safePosition);
    }
  };

  const scheduleSync = (input, delays = [0, 40, 120, 260, 520]) => {
    delays.forEach((delay) => {
      window.setTimeout(() => {
        normalizeInputValue(input);
        syncFieldState(input);
        applyValidationState(input);
      }, delay);
    });
  };

  const stopWatch = (input) => {
    const frameId = WATCHERS.get(input);
    if (frameId) {
      window.cancelAnimationFrame(frameId);
      WATCHERS.delete(input);
    }
  };

  const startWatch = (input) => {
    stopWatch(input);

    const startedAt = window.performance.now();

    const tick = () => {
      normalizeInputValue(input);
      syncFieldState(input);
      applyValidationState(input);

      const shouldContinue = document.activeElement === input && window.performance.now() - startedAt < 1800;

      if (!shouldContinue) {
        WATCHERS.delete(input);
        return;
      }

      const frameId = window.requestAnimationFrame(tick);
      WATCHERS.set(input, frameId);
    };

    const frameId = window.requestAnimationFrame(tick);
    WATCHERS.set(input, frameId);
  };

  const initForm = (form) => {
    if (!form || form.dataset.bnNewsletterReady === 'true') return;

    const input = form.querySelector(INPUT_SELECTOR);
    if (!input) return;

    form.dataset.bnNewsletterReady = 'true';

    form.setAttribute('autocomplete', 'on');

    input.setAttribute('autocomplete', 'email');
    input.setAttribute('inputmode', 'email');
    input.setAttribute('autocorrect', 'off');
    input.setAttribute('autocapitalize', 'off');
    input.setAttribute('spellcheck', 'false');
    input.setAttribute('enterkeyhint', 'send');
    input.setAttribute('pattern', '[^\\s@]+@[A-Za-z0-9-]+(?:\\.[A-Za-z0-9-]+)+');
    input.setAttribute('title', 'Ingresa un correo electrónico válido, por ejemplo nombre@dominio.com.');

    const syncNow = () => {
      normalizeInputValue(input);
      syncFieldState(input);
      applyValidationState(input);
    };

    const handleInteraction = () => {
      scheduleSync(input);
    };

    ['input', 'change', 'blur', 'keyup', 'paste', 'cut', 'mouseup', 'touchend', 'compositionend'].forEach((eventName) => {
      input.addEventListener(eventName, handleInteraction);
    });

    input.addEventListener('focus', () => {
      syncNow();
      startWatch(input);
    });

    input.addEventListener('beforeinput', () => {
      scheduleSync(input, [0, 24, 80]);
    });

    input.addEventListener('animationstart', (event) => {
      if (event.animationName === 'bn-newsletter-autofill' || event.animationName === 'footer-newsletter-autofill') {
        scheduleSync(input, [0, 50, 150, 350, 700]);
      }
    });

    input.addEventListener('invalid', () => {
      syncNow();
    });

    form.addEventListener('submit', (event) => {
      normalizeInputValue(input, { preserveSelection: false });
      syncFieldState(input);

      const isValid = applyValidationState(input);

      if (!isValid || !input.checkValidity()) {
        event.preventDefault();
        input.reportValidity();
        startWatch(input);
      }
    });

    document.addEventListener(
      'visibilitychange',
      () => {
        if (!document.hidden) {
          scheduleSync(input, [0, 80, 220]);
        }
      },
      { passive: true }
    );

    window.addEventListener(
      'pageshow',
      () => {
        scheduleSync(input, [0, 80, 220]);
      },
      { passive: true }
    );

    syncNow();
  };

  const initAll = (scope = document) => {
    scope.querySelectorAll(FORM_SELECTOR).forEach(initForm);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(), { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });
})();